/**
 * MobilePlayerRoster — chip strip + tap-to-expand sheet for the mobile
 * Play route.
 *
 * Desktop PlayerRoster bails on isMobile, so phone players had no view
 * of opponents (colour, name, turn, cube count). This component is the
 * mobile replacement: a 36px horizontal strip floating over the top of
 * the map (between PhaseCoach and the map proper), each chip showing
 * a colour disc + truncated name + territory count + current-turn glow.
 * Tapping a chip opens a small bottom-sheet (mirroring
 * MobileDashboardSheet's paper-card register) with full details.
 *
 * The strip dims to 0.4 opacity during combat-modal kinds so it stops
 * fighting ChalkQuizModal at z 1000.
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { AttackState, GameState, Player, PlayerColorKey, PlayerId } from '@qd/protocol';
import { fontStacks, playerColors, surfaces } from '@qd/design-tokens';
import { useReducedMotion } from '../../../hooks/useReducedMotion';

export const MOBILE_PLAYER_ROSTER_HEIGHT = 36;

interface MobilePlayerRosterProps {
  readonly gameState: GameState;
  readonly selfId: PlayerId | null;
  readonly attackState: AttackState;
  readonly topOffsetPx: number;
}

export function MobilePlayerRoster({
  gameState,
  selfId,
  attackState,
  topOffsetPx,
}: MobilePlayerRosterProps): ReactNode {
  // Memoise the territory count map — Zustand selectors that allocate
  // fresh collections inline have caused infinite render loops here
  // (CLAUDE.md §6 trap #4); the same hygiene applies to derived data.
  const territoryCounts = useMemo<Map<PlayerId, number>>(() => {
    const m = new Map<PlayerId, number>();
    for (const t of Object.values(gameState.territories)) {
      if (t.controllingPlayerId) m.set(t.controllingPlayerId, (m.get(t.controllingPlayerId) ?? 0) + 1);
    }
    return m;
  }, [gameState.territories]);

  const currentPlayerId = gameState.playerOrder[gameState.currentPlayerIdx];
  const [expandedId, setExpandedId] = useState<PlayerId | null>(null);

  // Drop the expanded sheet if its target player vanished.
  useEffect(() => {
    if (expandedId !== null && !gameState.players.some((p) => p.id === expandedId)) {
      setExpandedId(null);
    }
  }, [gameState.players, expandedId]);

  const ak = attackState.kind;
  const inCombatModal =
    ak === 'combatActive' || ak === 'combatResolved' || ak === 'territoryConquered';

  const expanded =
    expandedId !== null ? (gameState.players.find((p) => p.id === expandedId) ?? null) : null;

  return (
    <>
      <div
        role="list"
        aria-label="Players in this match"
        style={{
          position: 'fixed',
          top: topOffsetPx,
          left: 0,
          right: 0,
          height: MOBILE_PLAYER_ROSTER_HEIGHT,
          zIndex: 7,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          pointerEvents: 'none',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          opacity: inCombatModal ? 0.4 : 1,
          transition: 'opacity 180ms ease',
          background: `linear-gradient(180deg, ${hexAlpha(surfaces.paper, 0.78)} 0%, ${hexAlpha(surfaces.paper, 0.32)} 100%)`,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          borderBottom: `1px dashed ${hexAlpha(surfaces.graphite, 0.4)}`,
        }}
      >
        {gameState.players.map((p) => (
          <PlayerChip
            key={p.id}
            player={p}
            swatch={playerColors[p.color as PlayerColorKey].hex}
            isSelf={p.id === selfId}
            isCurrent={p.id === currentPlayerId}
            territoryCount={territoryCounts.get(p.id) ?? 0}
            disabled={inCombatModal}
            onTap={() => setExpandedId(p.id)}
          />
        ))}
      </div>

      <AnimatePresence>
        {expanded !== null ? (
          <PlayerDetailSheet
            key={expanded.id}
            player={expanded}
            isSelf={expanded.id === selfId}
            isCurrent={expanded.id === currentPlayerId}
            territoryCount={territoryCounts.get(expanded.id) ?? 0}
            onClose={() => setExpandedId(null)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}

// -----------------------------------------------------------------------------
// PlayerChip — single tap target inside the strip.
// -----------------------------------------------------------------------------

interface PlayerChipProps {
  readonly player: Player;
  readonly swatch: string;
  readonly isSelf: boolean;
  readonly isCurrent: boolean;
  readonly territoryCount: number;
  readonly disabled: boolean;
  readonly onTap: () => void;
}

function PlayerChip({ player, swatch, isSelf, isCurrent, territoryCount, disabled, onTap }: PlayerChipProps): ReactNode {
  const max = isSelf ? 9 : 6;
  const display = player.name.length > max ? `${player.name.slice(0, max - 1)}…` : player.name;
  return (
    <button
      type="button"
      role="listitem"
      onClick={disabled ? undefined : onTap}
      disabled={disabled}
      aria-label={`${player.name}, ${territoryCount} territories${isCurrent ? ', current turn' : ''}${isSelf ? ', you' : ''}.`}
      style={{
        flex: '0 0 auto',
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '0 8px 0 6px',
        borderRadius: 14,
        border: `1px solid ${isCurrent ? swatch : hexAlpha(surfaces.graphite, 0.45)}`,
        background: isCurrent ? hexAlpha(swatch, 0.18) : hexAlpha(surfaces.paper, 0.85),
        color: surfaces.ink,
        fontFamily: fontStacks.mono,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        cursor: disabled ? 'default' : 'pointer',
        pointerEvents: 'auto',
        boxShadow: isCurrent ? `0 0 0 2px ${hexAlpha(swatch, 0.35)}` : 'none',
        opacity: player.eliminated ? 0.45 : 1,
        textDecoration: player.eliminated ? 'line-through' : 'none',
        transition: 'box-shadow 160ms ease, background-color 160ms ease',
      }}
    >
      <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: swatch, border: `1px solid ${hexAlpha(surfaces.ink, 0.5)}`, flexShrink: 0, boxShadow: isCurrent ? `0 0 5px ${swatch}` : 'none' }} />
      <span style={{ whiteSpace: 'nowrap', maxWidth: isSelf ? 78 : 56, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {display}
        {isSelf ? <span style={{ marginLeft: 3, opacity: 0.65 }}>·you</span> : null}
      </span>
      <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: hexAlpha(surfaces.ink, 0.08), color: surfaces.ink, fontSize: 10, fontWeight: 700 }}>{territoryCount}</span>
      {!player.isConnected ? (
        <span aria-label="disconnected" title="Disconnected" style={{ width: 6, height: 6, borderRadius: '50%', background: surfaces.waxRed, flexShrink: 0 }} />
      ) : null}
    </button>
  );
}

// -----------------------------------------------------------------------------
// PlayerDetailSheet — small paper-card bottom sheet, same visual register
// as MobileDashboardSheet (drag handle, dashed-border header, ink type).
// -----------------------------------------------------------------------------

interface PlayerDetailSheetProps {
  readonly player: Player;
  readonly isSelf: boolean;
  readonly isCurrent: boolean;
  readonly territoryCount: number;
  readonly onClose: () => void;
}

function PlayerDetailSheet({ player, isSelf, isCurrent, territoryCount, onClose }: PlayerDetailSheetProps): ReactNode {
  const reduce = useReducedMotion();
  const meta = playerColors[player.color as PlayerColorKey];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const status = player.eliminated ? 'Eliminated' : player.isConnected ? 'Connected' : 'Disconnected';
  const rows: ReadonlyArray<readonly [string, string]> = [
    ['Color', meta.name],
    ['Turn', isCurrent ? 'Current' : 'Waiting'],
    ['Territories', String(territoryCount)],
    ['Reserve cubes', String(player.reserveCubes)],
    ['Status', status],
  ];

  return (
    <motion.div style={{ position: 'fixed', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pointerEvents: 'none' }}>
      <motion.button
        type="button"
        aria-label="Close player details"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduce ? 0 : 0.16 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(2,132,199,0.45)', border: 'none', padding: 0, cursor: 'pointer', pointerEvents: 'auto' }}
      />
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={`${player.name} details`}
        initial={reduce ? { opacity: 0 } : { y: '100%' }}
        animate={reduce ? { opacity: 1 } : { y: 0 }}
        exit={reduce ? { opacity: 0 } : { y: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 32, mass: 0.8 }}
        style={{ position: 'relative', background: surfaces.paper, color: surfaces.ink, borderTopLeftRadius: 16, borderTopRightRadius: 16, boxShadow: '0 -16px 36px rgba(0,0,0,0.45)', padding: '10px 16px', paddingBottom: `calc(16px + env(safe-area-inset-bottom, 0px))`, pointerEvents: 'auto' }}
      >
        <div aria-hidden style={{ margin: '0 auto 10px', width: 40, height: 4, borderRadius: 2, background: surfaces.graphite, opacity: 0.32 }} />
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 10, borderBottom: `1px dashed rgba(30,41,59,0.18)` }}>
          <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', background: meta.hex, border: `1.5px solid rgba(0,0,0,0.5)`, flexShrink: 0, boxShadow: isCurrent ? `0 0 8px ${meta.hex}` : 'none' }} />
          <h2 style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: fontStacks.display, fontSize: 16, fontWeight: 700, letterSpacing: '0.04em', color: surfaces.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {player.name}
            {isSelf ? <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.6 }}>(you)</span> : null}
          </h2>
          <button type="button" aria-label="Close player details" onClick={onClose} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: `1px dashed ${surfaces.graphite}`, borderRadius: 6, color: surfaces.ink, fontFamily: fontStacks.mono, fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, flexShrink: 0 }}>✕</button>
        </header>
        <dl style={{ margin: 0, paddingTop: 10, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 6, fontFamily: fontStacks.mono, fontSize: 12, color: surfaces.ink }}>
          {rows.map(([label, value]) => (
            <Fragment key={label}>
              <dt style={{ opacity: 0.6, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}>{label}</dt>
              <dd style={{ margin: 0, fontWeight: 700 }}>{value}</dd>
            </Fragment>
          ))}
        </dl>
      </motion.section>
    </motion.div>
  );
}

function hexAlpha(hex: string, alpha: number): string {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
